/**
 * Google One Tap beside the sign-in button.
 *
 * One Tap is Google's floating "continue as …" prompt. It is a convenience
 * laid over the door, never a door of its own: where it cannot show — phones
 * and tablets, unsupported browsers, a user who dismissed it once before —
 * nothing renders, nothing breaks, and the button below stays the way in.
 * Slackify, the reference implementation, went further and skipped the prompt
 * on every mobile user agent; that gate is kept here.
 */

interface GoogleCredentialResponse {
  credential: string
}

interface GooglePromptNotification {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
  isDismissedMoment?: () => boolean
  getDismissedReason?: () => string
}

interface GoogleIdentityId {
  initialize: (config: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
    context?: 'signin' | 'signup' | 'use'
    /** Whether the prompt routes through Chrome's FedCM. Off here, matching
     * the reference implementation — see the initialize call. */
    use_fedcm_for_prompt?: boolean
  }) => void
  prompt: (listener?: (notification: GooglePromptNotification) => void) => void
  cancel: () => void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdentityId } }
  }
}

/** One Tap is a desktop surface. Phones and tablets keep the button, which
 * works the same everywhere; the prompt is never even attempted there. */
const MOBILE_USER_AGENT = /iPhone|iPad|iPod|Android/i

export function oneTapCandidate(): boolean {
  if (typeof window === "undefined") return false
  if (MOBILE_USER_AGENT.test(window.navigator.userAgent)) {
    console.info("[one-tap] not attempted: this device keeps the button")
    return false
  }
  return true
}

/** Google's script on demand — already present, or fetched once now. */
function loadGoogleSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve()
      return
    }
    const existing = document.querySelector<HTMLScriptElement>('script[src^="https://accounts.google.com/gsi/client"]')
    const script =
      existing ??
      (() => {
        const added = document.createElement("script")
        added.src = "https://accounts.google.com/gsi/client"
        added.async = true
        document.head.appendChild(added)
        return added
      })()
    script.addEventListener("load", () => resolve())
    script.addEventListener("error", () => {
      console.info("[one-tap] Google's script failed to load — an extension or network filter may be blocking accounts.google.com")
      reject(new Error("Google Identity Services failed to load"))
    })
  })
}

export interface OneTapHandle {
  cancel: () => void
}

/**
 * Ask Google to show the prompt. Resolves to a handle that withdraws it;
 * `null` when this device is not a candidate or the script would not load —
 * both quiet outcomes the button already covers.
 */
export async function startOneTap(options: {
  clientId: string
  onCredential: (credential: string) => void
}): Promise<OneTapHandle | null> {
  if (!oneTapCandidate()) return null
  try {
    await loadGoogleSdk()
  } catch {
    return null
  }
  const id = window.google?.accounts.id
  if (!id) {
    console.info("[one-tap] Google's script loaded but the sign-in API is missing")
    return null
  }

  id.initialize({
    client_id: options.clientId,
    callback: (response) => options.onCredential(response.credential),
    auto_select: false,
    cancel_on_tap_outside: true,
    context: 'signin',
    // The reference implementation (Slackify) runs the legacy prompt and that
    // is the one proven to display on this studio's browsers; FedCM's prompt
    // is additionally gated by Chrome's own sign-in permission.
    use_fedcm_for_prompt: false,
  })
  // The prompt's moments, logged so the DevTools console answers "why did
  // nothing show": a skip names no reason (browser gate or cooldown), a
  // dismissal carries one.
  id.prompt((notification) => {
    if (notification.isDismissedMoment?.()) {
      console.info(`[one-tap] prompt dismissed (${notification.getDismissedReason?.() ?? 'unknown'})`)
    } else if (notification.isSkippedMoment?.()) {
      console.info(
        '[one-tap] prompt skipped by the browser — check chrome://settings/content/federatedIdentityApi and any recent dismissal cooldown',
      )
    } else {
      console.info('[one-tap] prompt shown')
    }
  })
  return {
    cancel: () => {
      try {
        id.cancel()
      } catch {
        // Already gone — there was never anything to withdraw.
      }
    },
  }
}
